module Client.Admin.UserInfoSections.InactiveUsers

open Client
open Elmish
open Shared.Admin.UserInfo
open Illy.SafeStack.Client
open Shared
open Fable.React
open Fable.React.Props
open Illy.SafeStack.Client.jQueryUI
open Shared.Admin.UserGrid
open System
open Browser.Dom

type Model =
    {
        Count: int
        Loading: bool
        GettingData: bool
        GettingFirstPage: bool
        SearchString: Text.Model
        TotalCount: int 
        CanLoadMoreData: bool
        LastPage: int
        Data: Admin.UserInfo.NamedUserLogin list 
        DebounceSearch: Option<string> Debounce.Model
    }
and Msg =
    | LoadedUsers of Admin.UserInfo.NamedUserLogin list * int
    | LoadedCount of int
    | ApiError of ApiError
    | CloseOverlay
    | SearchStringMsg of Text.Msg
    | DebounceSearchMsg of Option<string> Debounce.Msg
    | LoadMoreResults
    | RefreshGrid

type Event =
    | ApiError of ApiError
    | CloseOverlay

module private Initialisations = 
    let initSearchString value =
        Text.CustomSettings.defaultOptions()
        |> Text.init "inactive-user-grid-search" "Search String" (Some "Type here to search") 5000. false value

let getGridDataCmd pageNo searchStr =
    Api.secureCall<IUserInfoApi,_,_,_> SecuredRoute.Admin (fun api -> api.getInactiveUsersPaged searchStr) pageNo LoadedUsers Msg.ApiError

let init() =
    {
        Loading = true
        Count = 0
        GettingData = true
        GettingFirstPage = true
        SearchString = Initialisations.initSearchString None
        TotalCount = 0
        Data = []
        CanLoadMoreData = false
        LastPage = 1
        DebounceSearch = Debounce.init (TimeSpan.FromMilliseconds 300.) None
    }, Cmd.batch [
        yield Api.secureCall<IUserInfoApi,_,_,_> SecuredRoute.Admin (fun api -> api.getInactiveUsersCount) () LoadedCount Msg.ApiError
    ]

let showGrid model =
    {
        model with
            GettingData = true
            GettingFirstPage = true
    }, getGridDataCmd 1 None

let update msg model =
    match msg with
    | Msg.CloseOverlay -> model, CloseOverlay |> SideEffect.Event
    | Msg.ApiError err -> model, err |> ApiError |> SideEffect.Event
    | LoadedUsers (users, count) ->
        {
            model with
                Loading = false
                GettingFirstPage = false 
                GettingData = false
                Data = if model.GettingFirstPage then users else model.Data @ users 
                TotalCount = count
                CanLoadMoreData = model.LastPage * PAGE_SIZE <= count
        }, SideEffect.none
    | LoadedCount count ->
        {
            model with
                Count = count
                Loading = false
        }, SideEffect.none
    | SearchStringMsg cMsg ->
        let m, c = Text.update cMsg model.SearchString
        let hasSearchStrChanged = Text.toString m <> Text.toString model.SearchString
        {model with 
            SearchString = m
            GettingData = hasSearchStrChanged
            GettingFirstPage = hasSearchStrChanged
            LastPage = if hasSearchStrChanged then 1 else model.LastPage
        }, Cmd.batch [
            yield Cmd.map SearchStringMsg c
            if hasSearchStrChanged then yield Debounce.inputCmd (Text.toString m) DebounceSearchMsg
        ] |> SideEffect.Cmd
    | DebounceSearchMsg dMsg -> 
        let m, c = 
            Debounce.updateWithCmd
                dMsg
                DebounceSearchMsg
                model.DebounceSearch
                (fun x -> 
                    { model with
                        DebounceSearch = x 
                    })
                (getGridDataCmd 1)
        m , SideEffect.Cmd c
    | RefreshGrid ->
        {model with 
            GettingData = true
            GettingFirstPage = true
            LastPage = 1
        }, model.SearchString |> Text.toString |> getGridDataCmd 1 |> SideEffect.Cmd
    | LoadMoreResults -> 
        if not model.CanLoadMoreData || model.GettingData then model, SideEffect.none
        else 
            let newPage =  model.LastPage + 1
            {model with
                GettingData = true 
                LastPage = newPage
            }, model.SearchString |> Text.toString |> getGridDataCmd newPage |> SideEffect.Cmd

let callDebounce = debounce 500

let viewContent model dispatch = 
    div [
        Key "user-search-container"
        ClassName "user-search-container"
    ] [
        yield Text.textInput false model.SearchString (SearchStringMsg >> dispatch)

        yield div [
            Key "user-search-results-container"
            Id "user-search-results-container"
            Class "user-search-results"
            Style [
                Height "300px"
                OverflowY OverflowOptions.Scroll
            ]
            OnScroll (fun _ -> 
                if not model.GettingData && model.CanLoadMoreData then
                    let parent = document.getElementById("user-search-results-container")
                    if (parent.scrollHeight - parent.scrollTop - parent.clientHeight) <= 1. then
                        callDebounce
                            (fun (changeEv,_) -> dispatch changeEv)
                            LoadMoreResults
                            ()
            )
        ] [
            yield jQueryUI.simpleTable 
                "No users found."
                [
                    Some [ str "Name" ] ===> fun (x: NamedUserLogin) -> [ 
                        str x.Forename 
                        str " " 
                        str x.Surname 
                    ]
                    Some [ str "Username" ] ===> fun (user: NamedUserLogin) -> [ str user.Username ]
                    Some [ str "Last Login" ] ===> fun (user: NamedUserLogin) -> [ 
                        match user.LoginDate with
                        | None -> str "Never"
                        | Some d ->
                            str (d |> illyDateTime) 
                            str " ("
                            str (d |> Moment.fromNow)
                            str ")"
                    ]
                ]
                model.GettingFirstPage
                model.Data

            if model.GettingData && not model.GettingFirstPage then 
                yield jQueryUI.spinner
        ]
    ]

let isLoadingMoreResults (m: Model) = m.GettingData && not m.GettingFirstPage

let view (model: Model) dispatch =
    let title = 
        if isLoadingMoreResults model 
        then "Loading more results..."
        else "Users Not Logged in For 28 Days"

    jQueryUI.modal
        title
        2500
        Msg.CloseOverlay
        dispatch
        (Some 1000)
        [
            viewContent model dispatch
        ]
